/**
 * HDM Custom Events Context.
 * This provider wraps the core EventsProvider to add custom event handling
 * for customized handlers without duplicating core logic.
 *
 * Added following event handlers:
 *
 *  onHDMLoginComplete
 *  onHDMCreateAccountComplete
 */
import {
	EventsContext as CoreEventsContext,
	EventsContextType as CoreEventsContextType,
	EventsProvider as CoreEventsProvider,
} from '@/core/data/context/events';
import { hdmCreateAccountCompleteDelegator } from '@/data/events/delegators/HDMCreateAccountComplete';
import { hdmLoginCompleteDelegator } from '@/data/events/delegators/HDMLoginComplete';
import { noop } from 'lodash';
import {
	FC,
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

/**
 * Define types for only the custom events.
 */
type CustomEventHandlers = {
	onHDMLoginComplete: typeof hdmLoginCompleteDelegator;
	onHDMCreateAccountComplete: typeof hdmCreateAccountCompleteDelegator;
};

/**
 * Infer the core event handlers by omitting non-handler properties from the core context type.
 */
type CoreEventHandlers = Omit<
	CoreEventsContextType,
	'registerEvent' | 'addEventData' | 'clearEventData' | 'eventData'
>;
type EventHandlers = CoreEventHandlers & CustomEventHandlers;

type RegisterEvent = <T extends keyof EventHandlers>(
	eventType: T,
	handler: EventHandlers[T]
) => void;

/**
 * Create a new, extended context type by merging core and custom types.
 */
export type EventsContextType = Omit<CoreEventsContextType, 'registerEvent'> &
	CustomEventHandlers & {
		registerEvent: RegisterEvent;
	};

/**
 * Create a new context that will hold the extended value.
 */
export const EventsContext = createContext<EventsContextType>(null as any);
const Provider = EventsContext.Provider;

/**
 * A component that consumes the core context and provides an extended version.
 */
const CustomEventsProvider: FC<PropsWithChildren> = ({ children }) => {
	const coreEvents = useContext(CoreEventsContext);

	const [onHDMLoginComplete, setOnHDMLoginComplete] = useState<
		CustomEventHandlers['onHDMLoginComplete']
	>(() => noop as CustomEventHandlers['onHDMLoginComplete']);

	const [onHDMCreateAccountComplete, setOnHDMCreateAccountComplete] = useState<
		CustomEventHandlers['onHDMCreateAccountComplete']
	>(() => noop as CustomEventHandlers['onHDMCreateAccountComplete']);

	const registerEvent = useCallback(
		(eventType: keyof EventHandlers, handler: EventHandlers[keyof EventHandlers]) => {
			switch (eventType) {
				case 'onHDMLoginComplete':
					setOnHDMLoginComplete(() => handler as CustomEventHandlers['onHDMLoginComplete']);
					break;
				case 'onHDMCreateAccountComplete':
					setOnHDMCreateAccountComplete(
						() => handler as CustomEventHandlers['onHDMCreateAccountComplete']
					);
					break;
				default:
					coreEvents.registerEvent(eventType as keyof CoreEventHandlers, handler as any);
					break;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const value = useMemo(
		() => ({
			...coreEvents,
			registerEvent,
			onHDMLoginComplete,
			onHDMCreateAccountComplete,
		}),
		[coreEvents, registerEvent, onHDMLoginComplete, onHDMCreateAccountComplete]
	);

	return <Provider value={value as EventsContextType}>{children}</Provider>;
};

export const useEventsContext = () => useContext(EventsContext);

/**
 * The main export. It wraps the Core provider with our custom provider.
 */
export const EventsProvider: FC<PropsWithChildren> = ({ children }) => (
	<CoreEventsProvider>
		<CustomEventsProvider>{children}</CustomEventsProvider>
	</CoreEventsProvider>
);
